import React from "react";
import {
  Badge,
  Box,
  Button,
  Divider,
  Drawer,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded";
import FilterAltOffRoundedIcon from "@mui/icons-material/FilterAltOffRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import IconButton from "@mui/material/IconButton";
import { responsiveBottomDrawerPaperSx } from "../../theme/webProfitResponsive";

/**
 * Toolbar compartida para listados. En pantallas chicas mantiene la búsqueda
 * visible y mueve filtros secundarios a un drawer táctil; desde tablet en
 * adelante conserva los controles en línea.
 */
export default function ResponsiveListControls({
  search,
  children,
  actions = null,
  activeFilterCount = 0,
  onClear,
  filterLabel = "Filtros",
  drawerTitle = "Filtrar listado",
  sx,
}) {
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down("md"), { noSsr: true });
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    if (!mobile) setOpen(false);
  }, [mobile]);

  if (!mobile) {
    return (
      <Stack
        direction="row"
        spacing={1}
        useFlexGap
        flexWrap="wrap"
        alignItems="center"
        sx={{ width: "100%", "& > *": { minWidth: 0 }, ...sx }}
      >
        <Box sx={{ flex: "1 1 320px", minWidth: 220 }}>{search}</Box>
        {children}
        {actions}
      </Stack>
    );
  }

  return (
    <>
      <Stack direction="row" spacing={1} alignItems="stretch" sx={{ width: "100%", ...sx }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>{search}</Box>
        <Badge
          color="primary"
          badgeContent={activeFilterCount}
          invisible={!activeFilterCount}
          overlap="rectangular"
        >
          <Button
            variant={activeFilterCount ? "contained" : "outlined"}
            onClick={() => setOpen(true)}
            startIcon={<FilterListRoundedIcon />}
            aria-label={`${filterLabel}${activeFilterCount ? `, ${activeFilterCount} activos` : ""}`}
            sx={{ minWidth: 112, px: 1.25, whiteSpace: "nowrap", textTransform: "none", fontWeight: 900 }}
          >
            {filterLabel}
          </Button>
        </Badge>
        {actions ? <Box sx={{ display: "flex", alignItems: "stretch" }}>{actions}</Box> : null}
      </Stack>

      <Drawer
        anchor="bottom"
        open={open}
        onClose={() => setOpen(false)}
        ModalProps={{ keepMounted: true }}
        PaperProps={{ sx: responsiveBottomDrawerPaperSx }}
      >
        <Stack sx={{ minHeight: 0, maxHeight: "inherit" }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ px: 2, py: 1.5 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 950 }}>{drawerTitle}</Typography>
              {activeFilterCount ? (
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {activeFilterCount} {activeFilterCount === 1 ? "filtro activo" : "filtros activos"}
                </Typography>
              ) : null}
            </Box>
            <IconButton onClick={() => setOpen(false)} aria-label="Cerrar filtros">
              <CloseRoundedIcon />
            </IconButton>
          </Stack>
          <Divider />
          <Stack spacing={1.25} sx={{ p: 2, overflowY: "auto", "& .MuiFormControl-root, & .MuiTextField-root": { width: "100%" } }}>
            {children}
          </Stack>
          <Divider />
          <Stack direction="row" spacing={1} sx={{ p: 2, pt: 1.5 }}>
            {onClear ? (
              <Button
                variant="outlined"
                startIcon={<FilterAltOffRoundedIcon />}
                onClick={() => {
                  onClear();
                  setOpen(false);
                }}
                disabled={!activeFilterCount}
                sx={{ flex: 1, textTransform: "none", fontWeight: 850 }}
              >
                Limpiar
              </Button>
            ) : null}
            <Button
              variant="contained"
              onClick={() => setOpen(false)}
              sx={{ flex: 1, textTransform: "none", fontWeight: 900 }}
            >
              Ver resultados
            </Button>
          </Stack>
        </Stack>
      </Drawer>
    </>
  );
}
